// Click-to-sort for every table in the console, without each page wiring its own state. A header click sorts that table's rows (numbers by value, everything else
// by text, empty cells last); a second click reverses; a third restores the original order. Tables that sort on the SERVER (they have `th.sortable`) or opt out
// (`data-nosort`) are left alone. For a paged table this sorts the rows on the current page only. The sort is re-applied when the page re-renders the rows.

type State = { col: number; dir: 1 | -1; original: HTMLTableRowElement[]; restore?: boolean };
const states = new WeakMap<HTMLTableElement, State>();

const cellValue = (tr: HTMLTableRowElement, col: number): string => {
  const c = tr.cells[col];
  return c ? (c.getAttribute("data-sort") ?? c.textContent ?? "").trim() : "";
};
const asNumber = (s: string): number | null => {
  const t = s.replace(/[$,%\s]/g, "").replace(/^\((.*)\)$/, "-$1");
  return t !== "" && /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null;
};
const eligible = (t: HTMLTableElement) => !t.hasAttribute("data-nosort") && !t.querySelector("thead th.sortable") && !!t.tBodies[0];

function bodyRows(t: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(t.tBodies[0].rows).filter((r) => r.cells.length > 1 || !r.cells[0]?.colSpan || r.cells[0].colSpan < 2);
}

function apply(t: HTMLTableElement, st: State) {
  const rows = bodyRows(t);
  const sorted = st.restore ? st.original.filter((r) => rows.includes(r)) : [...rows].sort((a, b) => {
    const x = cellValue(a, st.col), y = cellValue(b, st.col);
    if (x === "" || x === "—") return y === "" || y === "—" ? 0 : 1;
    if (y === "" || y === "—") return -1;
    const nx = asNumber(x), ny = asNumber(y);
    return (nx != null && ny != null ? nx - ny : x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" })) * st.dir;
  });
  sorted.forEach((r) => t.tBodies[0].appendChild(r));
}

function mark(t: HTMLTableElement, col: number | null, dir: number) {
  t.querySelectorAll("thead th").forEach((h, i) => { if (i === col && dir) h.setAttribute("aria-sort", dir > 0 ? "ascending" : "descending"); else h.removeAttribute("aria-sort"); });
}

export function installSortableTables(): () => void {
  const onClick = (e: MouseEvent) => {
    const th = (e.target as HTMLElement | null)?.closest?.("thead th") as HTMLTableCellElement | null;
    if (!th || (e.target as HTMLElement).closest("input,button,select,a")) return;
    const t = th.closest("table") as HTMLTableElement | null;
    if (!t || !eligible(t)) return;
    const col = Array.from(th.parentElement!.children).indexOf(th);
    const prev = states.get(t);
    if (prev && prev.col === col && prev.dir === -1) {             // third click: back to the original order
      apply(t, { ...prev, restore: true }); states.delete(t); mark(t, null, 0); return;
    }
    const st: State = { col, dir: prev && prev.col === col ? -1 : 1, original: prev?.original ?? bodyRows(t) };
    states.set(t, st); apply(t, st); mark(t, col, st.dir);
  };
  let pending = false;
  const obs = new MutationObserver((muts) => {
    if (pending) return;
    const touched = new Set<HTMLTableElement>();
    for (const m of muts) { const t = (m.target as HTMLElement).closest?.("table") as HTMLTableElement | null; if (t && states.has(t)) touched.add(t); }
    if (!touched.size) return;
    pending = true;
    requestAnimationFrame(() => {
      obs.disconnect();
      touched.forEach((t) => { const st = states.get(t); if (!st || !document.contains(t)) return; st.original = bodyRows(t); apply(t, st); });
      obs.observe(document.body, { childList: true, subtree: true });
      pending = false;
    });
  });
  document.addEventListener("click", onClick);
  obs.observe(document.body, { childList: true, subtree: true });
  return () => { document.removeEventListener("click", onClick); obs.disconnect(); };
}
